import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import chauffeurService from '../../services/chauffeurService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/common/PageHeader';
import transporteurService from '../../services/transporteurService';
import FilterCard from '../../components/common/FilterCard';
import './ChauffeurList.css';
import { useI18n } from '../../i18n/I18nContext';
import { useFilter } from '../../context/FilterContext';

function ChauffeurList() {
  const [chauffeurs, setChauffeurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [availability, setAvailability] = useState('all');
  const [limitInfo, setLimitInfo] = useState({ limit: 0, count: 0 });
  const navigate = useNavigate();
  const { t } = useI18n(); // Get the translation function

  const { selectedTransporteurId } = useFilter();

  useEffect(() => {
    const fetchChauffeurs = async () => {
      try {
        setLoading(true);
        const data = await chauffeurService.getAllDrivers(selectedTransporteurId);
        setChauffeurs(data);
        setError(null);
      } catch (err) {
        setError(t('chauffeurs.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchChauffeurs();
    // load driver limit info
    (async () => {
      try {
        const info = await transporteurService.getDriverLimitInfo();
        setLimitInfo(info);
      } catch (_) {}
    })();
  }, [t, selectedTransporteurId]);

  const handleRowClick = (id) => {
    navigate(`/chauffeurs/view/${id}`);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation(); // Prevent row click when clicking delete button
    if (window.confirm(t('chauffeurs.confirmDelete'))) {
      try {
        await chauffeurService.deleteDriver(id);
        setChauffeurs(chauffeurs.filter(chauffeur => chauffeur.id !== id));
        // free one slot in the limit counter (if a limit is set)
        setLimitInfo(prev => ({
          ...prev,
          count: prev && typeof prev.count === 'number' ? Math.max(0, prev.count - 1) : 0
        }));
      } catch (err) {
        const rawMsg = err && err.message ? err.message : '';
        if (rawMsg.toLowerCase().includes('assigned to missions') || rawMsg.toLowerCase().includes('assigned to a mission')) {
          setError(t('chauffeurs.errors.deleteHasAssignments'));
        } else {
          setError(t('chauffeurs.errors.deleteFailed'));
        }
      }
    }
  };

  const handleAvailabilityChange = async (e, id, disponible) => {
    e.stopPropagation(); // Prevent row click when clicking availability button
    try {
      await chauffeurService.updateDriverAvailability(id, disponible);
      setChauffeurs(chauffeurs.map(chauffeur => 
        chauffeur.id === id ? { ...chauffeur, disponible } : chauffeur
      ));
    } catch (err) {
      setError(t('chauffeurs.errors.updateFailed'));
    }
  };

  const filteredChauffeurs = chauffeurs.filter(chauffeur => {
    const query = search.toLowerCase();
    const matchesText =
      chauffeur.firstname.toLowerCase().includes(query) ||
      chauffeur.lastname.toLowerCase().includes(query) ||
      chauffeur.email.toLowerCase().includes(query) ||
      chauffeur.telephone.toLowerCase().includes(query) ||
      chauffeur.licenseNumber?.toLowerCase().includes(query) ||
      chauffeur.enterpriseName?.toLowerCase().includes(query);

    const matchesAvailability =
      availability === 'all' ||
      (availability === 'available' && chauffeur.disponible) ||
      (availability === 'unavailable' && !chauffeur.disponible);

    return matchesText && matchesAvailability;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="chauffeur-list-container">
          <div className="container">
            {/* Loading state: keep layout consistent, no explicit spinner */}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="chauffeur-list-container">
        <div className="container">
          <PageHeader 
            title={t('chauffeurs.title')}
            subtitle={
              limitInfo.limit > 0
                ? t('chauffeurs.subtitleWithLimit', { count: limitInfo.count, limit: limitInfo.limit })
                : t('chauffeurs.subtitle')
            }
            buttonText={t('chauffeurs.addNew')}
            buttonTo="/chauffeurs/create"
            onButtonClick={() => {
              if (limitInfo.limit > 0 && limitInfo.count >= limitInfo.limit) {
                setError(t('chauffeurs.errors.limitReached'));
                return;
              }
              navigate('/chauffeurs/create');
            }}
          />

        {error && (
          <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

          <FilterCard>
            <div className="row g-3 align-items-end">
              <div className="col-lg-6">
                <div className="input-group search-input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('chauffeurs.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <select
                  className="form-select"
                  value={availability}
                  onChange={(e) => setAvailability(e.target.value)}
                >
                  <option value="all">{t('chauffeurs.status.all')}</option>
                  <option value="available">{t('chauffeurs.status.availableOnly')}</option>
                  <option value="unavailable">{t('chauffeurs.status.unavailableOnly')}</option>
                </select>
              </div>
              <div className="col-lg-3">
                <button
                  className="clear-btn w-100"
                  onClick={() => {
                    setSearch('');
                    setAvailability('all');
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  {t('chauffeurs.clearFilters')}
                </button>
              </div>
            </div>
          </FilterCard>

          {/* Results Summary */}
          {filteredChauffeurs.length > 0 && (
            <div className="mb-3">
              <small className="text-muted">
                {t('chauffeurs.showingResults', { 
                  filtered: filteredChauffeurs.length, 
                  total: chauffeurs.length 
                })}
              </small>
          </div>
          )}

          {/* Chauffeurs Table */}
        {filteredChauffeurs.length === 0 ? (
            <div className="c-table-card card">
              <div className="card-body">
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <i className="bi bi-person-x"></i>
                  </div>
                  <h3 className="empty-state-title">
                    {chauffeurs.length === 0 ? t('chauffeurs.emptyState.noChauffeurs') : t('chauffeurs.emptyState.noResults')}
                  </h3>
                  <p className="empty-state-text">
            {chauffeurs.length === 0
                      ? t('chauffeurs.emptyState.addFirst')
                      : t('chauffeurs.emptyState.adjustSearch')}
                  </p>
                </div>
              </div>
          </div>
        ) : (
            <div className="c-table-card card">
          <div className="table-responsive">
                <table className="c-table table align-middle">
                  <thead>
                <tr>
                      <th>{t('chauffeurs.table.driver')}</th>
                      <th>{t('chauffeurs.table.contact')}</th>
                      <th>{t('chauffeurs.table.license')}</th>
                  <th>{t('chauffeurs.table.enterprise')}</th>
                  <th>{t('chauffeurs.table.status')}</th>
                  <th className="text-end">{t('chauffeurs.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredChauffeurs.map((chauffeur) => (
                  <tr 
                    key={chauffeur.id}
                    onClick={() => handleRowClick(chauffeur.id)}
                    title={t('chauffeurs.table.clickToEdit')}
                    className={!chauffeur.active ? 'chauffeur-row-inactive' : ''}
                  >
                        <td data-label={t('chauffeurs.table.driver')}>
                      <div className="d-flex align-items-center">
                            <div className="avatar-circle">
                          {chauffeur.firstname[0]}{chauffeur.lastname[0]}
                        </div>
                            <div className="ms-1">
                              <span className="chauffeur-name">{chauffeur.firstname} {chauffeur.lastname}</span>
                        </div>
                      </div>
                    </td>
                        <td data-label={t('chauffeurs.table.contact')}>
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                            <span className="chauffeur-contact-email">{chauffeur.email}</span>
                            <span className="chauffeur-info">{chauffeur.telephone}</span>
                          </div>
                        </td>
                        <td data-label={t('chauffeurs.table.license')}>
                          <span className="chauffeur-name">{chauffeur.licenseNumber}</span>
                        </td>
                        <td data-label={t('chauffeurs.table.enterprise')}>
                          <span className="chauffeur-name">{chauffeur.enterpriseName}</span>
                        </td>
                        <td data-label={t('chauffeurs.table.status')}>
                          <span className={`status-badge ${chauffeur.disponible ? 'status-available' : 'status-unavailable'}`}>
                            {chauffeur.disponible ? t('chauffeurs.status.available') : t('chauffeurs.status.unavailable')}
                          </span>
                        </td>
                        <td data-label={t('chauffeurs.table.actions')}>
                          <div className="action-buttons">
                            <button
                              onClick={(e) => handleAvailabilityChange(e, chauffeur.id, !chauffeur.disponible)}
                              className={`btn-action ${
                                chauffeur.disponible ? 'btn-toggle-unavailable' : 'btn-toggle-available'
                              }`}
                              title={chauffeur.active === false 
                                ? t('chauffeurs.actions.cannotChangeStatusDeactivated') || 'Cannot change status: Account is deactivated'
                                : (chauffeur.disponible ? t('chauffeurs.actions.setUnavailable') : t('chauffeurs.actions.setAvailable'))}
                              disabled={chauffeur.active === false}
                              style={chauffeur.active === false ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            >
                              <i className={`bi ${chauffeur.disponible ? 'bi-x-circle' : 'bi-check-circle'}`}></i>
                            </button>
                          </div>
                        </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </div>
          </div>
        )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ChauffeurList;